import { FC, useMemo } from 'react';

import {
  ChannelLedgerListResponse,
  DEDUCT_SOURCE_LABEL,
  GiftChannelLedgerEntry,
  GiftChannelLedgerType,
  LEDGER_TYPE_CONFIG,
  ledgerGiftItemNames,
  MANUAL_LEDGER_TYPES,
} from '@shared/api/giftChannelLedger/giftChannelLedger.interface';

import { kstDateTimeLabel } from '../../utils/kstTime.utils';

interface Props {
  ledger: ChannelLedgerListResponse;
  /** from 필터가 걸려 있으면 openingBalance가 "기간 이전 잔액"으로 의미를 가짐 */
  hasFromFilter: boolean;
  isAdminUser: boolean;
  deletingId: string | null;
  onDelete: (entry: GiftChannelLedgerEntry) => void;
}

const formatDateTime = (date: Date) => kstDateTimeLabel(new Date(date));

/** 항목 내용 설명 — Deduct는 상품명·수량×단가, 나머지는 메모 위주 */
const entryDetail = (entry: GiftChannelLedgerEntry): string => {
  const parts: string[] = [];
  const itemNames = ledgerGiftItemNames(entry);
  if (itemNames.length > 0) parts.push(itemNames.join(', '));
  if (entry.type === GiftChannelLedgerType.Deduct && entry.quantity) {
    parts.push(`${entry.quantity}건 × ${(entry.unitPriceWhole ?? 0).toLocaleString()}원`);
  }
  if (entry.memo) parts.push(entry.memo);
  return parts.join(' — ');
};

/** 채널 원장 테이블 — 최근 항목이 위로 오도록 역순 표시 (누적잔액은 서버 계산값 그대로) */
export const ChannelLedgerTable: FC<Props> = ({
  ledger,
  hasFromFilter,
  isAdminUser,
  deletingId,
  onDelete,
}) => {
  const rows = useMemo(() => [...ledger.entries].reverse(), [ledger.entries]);

  return (
    <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b bg-gray-50 px-4 py-2 text-xs text-gray-600">
        <span>
          {hasFromFilter ? '기간 시작 전 잔액' : '기초잔액'}{' '}
          <span className="font-semibold">
            {ledger.openingBalance.toLocaleString()}원
          </span>
        </span>
        <span>→</span>
        <span>
          {hasFromFilter ? '기간 말 잔액' : '현재 잔액'}{' '}
          <span className="font-semibold text-indigo-700">
            {ledger.closingBalance.toLocaleString()}원
          </span>
        </span>
        <span className="text-gray-400">· {ledger.entries.length}건</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
              <th className="px-4 py-2 font-medium">발생 시각</th>
              <th className="px-4 py-2 font-medium">유형</th>
              <th className="px-4 py-2 font-medium">내용</th>
              <th className="px-4 py-2 font-medium">처리자</th>
              <th className="px-4 py-2 text-right font-medium">금액</th>
              <th className="px-4 py-2 text-right font-medium">누적 잔액</th>
              {isAdminUser && <th className="px-2 py-2" />}
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((entry) => {
              const typeConfig = LEDGER_TYPE_CONFIG[entry.type];
              // 자동 기장분(source 존재 — 발송취소 환급 Restore 등)은 쿠폰 감사 기록과
              // 짝이라 서버가 삭제를 400 거부한다 — 버튼도 숨겨 맞춘다
              const deletable =
                isAdminUser &&
                MANUAL_LEDGER_TYPES.includes(entry.type) &&
                !entry.source;
              return (
                <tr key={entry._id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-600">
                    {formatDateTime(entry.occurredAt)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">
                    <span
                      className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${typeConfig.badgeClass}`}
                    >
                      {typeConfig.label}
                    </span>
                    {entry.source && entry.source !== 'deliver' && (
                      <span className="ml-1 text-[10px] text-gray-400">
                        {DEDUCT_SOURCE_LABEL[entry.source]}
                      </span>
                    )}
                  </td>
                  <td className="max-w-[320px] truncate px-4 py-2 text-xs text-gray-700">
                    {entryDetail(entry) || '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-500">
                    {entry.createdBy?.info?.name ?? entry.createdBy?.username ?? '—'}
                  </td>
                  <td
                    className={`whitespace-nowrap px-4 py-2 text-right text-xs font-semibold ${
                      entry.amount < 0 ? 'text-red-600' : 'text-emerald-600'
                    }`}
                  >
                    {entry.amount > 0 ? '+' : ''}
                    {entry.amount.toLocaleString()}원
                  </td>
                  <td
                    className={`whitespace-nowrap px-4 py-2 text-right text-xs font-medium ${
                      (entry.balanceAfter ?? 0) < 0 ? 'text-red-600' : 'text-gray-900'
                    }`}
                  >
                    {(entry.balanceAfter ?? 0).toLocaleString()}원
                  </td>
                  {isAdminUser && (
                    <td className="whitespace-nowrap px-2 py-2 text-right">
                      {deletable && (
                        <button
                          type="button"
                          onClick={() => onDelete(entry)}
                          disabled={deletingId === entry._id}
                          className="rounded px-1.5 py-0.5 text-[11px] text-red-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        >
                          {deletingId === entry._id ? '삭제중…' : '삭제'}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
